import React, { useState } from 'react';
import { ShieldCheck, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API, useAuth } from '../contexts/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';

import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

const AuthorizationModal = ({ isOpen, onClose, onAuthorized, action = "eliminar productos" }) => {
  const { getAuthHeaders } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Ingresa email y contraseña');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/authorize`,
        { email, password },
        { headers: getAuthHeaders() }
      );

      if (response.data.authorized) {
        toast.success(`Autorizado por: ${response.data.authorized_by}`);
        onAuthorized(response.data);
        handleClose();
      }
    } catch (error) {
      console.error('Authorization error:', error);
      toast.error(getErrorMessage(error, 'Autorización denegada'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl">Autorización Requerida</DialogTitle>
              <p className="text-sm text-gray-600">Se necesita permiso de administrador</p>
            </div>
          </div>
        </DialogHeader>

        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-orange-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-orange-900">Acción de seguridad</p>
              <p className="text-sm text-orange-700">
                Para {action}, se requiere autorización de un administrador o supervisor.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="auth-email">Email del Administrador</Label>
            <Input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@restaurant.com"
              className="rounded-xl"
              autoFocus
              required
            />
          </div>

          <div>
            <Label htmlFor="auth-password">Contraseña</Label>
            <Input
              id="auth-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="rounded-xl"
              required
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 rounded-xl"
            >
              {loading ? 'Verificando...' : 'Autorizar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AuthorizationModal;
